import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Dashboard.css';
import './shared.css';
import { useTranslation } from 'react-i18next';
import DeviceRegistration from './DeviceRegistration';
import Footer from './Footer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function Dashboard({ onStartSlideshow, onLogout, subscriptionId: initialSubscriptionId, email, mode, onBackToModeSelection }) {
  const { t } = useTranslation();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const devicesSectionRef = useRef(null);

  // scheduleMap: id -> schedule object, loaded once from /api/schedule/available
  const [scheduleMap, setScheduleMap] = useState({});
  const [schedulesLoading, setSchedulesLoading] = useState(true);

  // per-subscription selected scheduleId
  const [selectedScheduleBySub, setSelectedScheduleBySub] = useState({});

  const hasConfiguredMac = (schedule) => String(schedule?.slideshowDeviceMac || '').trim().length > 0;

  // Return schedule objects for a subscription — only browser-category schedules are usable in browser mode

  const getSchedulesForSubscription = useCallback((subscription) => {
    return (subscription.assignedScheduleIds || [])
      .map(id => scheduleMap[id])
      .filter(Boolean)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ja'));
  }, [scheduleMap]);

  // Device size codes ordered smallest to largest (matches gallery-pipeline's resolutionToDeviceSize)
  const DEVICE_SIZE_ORDER = ['13', '25', '28', '31', '40'];
  const getScheduleSizeCode = (schedule) => {
    for (const code of DEVICE_SIZE_ORDER) {
      if (schedule?.[`slideshowDeviceMac${code}`]) return code;
    }
    return null;
  };

  // Picks the single largest-resolution schedule from a list (browser can render any
  // size, so there's no need to offer every resolution separately - largest looks best)
  const pickLargestSchedule = (list) => {
    if (list.length === 0) return [];
    return [list.reduce((best, s) => {
      const bestIdx = DEVICE_SIZE_ORDER.indexOf(getScheduleSizeCode(best));
      const idx = DEVICE_SIZE_ORDER.indexOf(getScheduleSizeCode(s));
      return idx > bestIdx ? s : best;
    })];
  };

  // Returns schedules grouped by groupName then orientation, keeping only the largest
  // resolution per group+orientation, since the browser viewer can scale to any size
  const groupSchedulesByGroup = (scheduleList) => {
    const groups = new Map();
    for (const s of scheduleList) {
      const groupKey = s.groupName || '(グループなし)';
      if (!groups.has(groupKey)) groups.set(groupKey, { landscape: [], portrait: [] });
      const orient = String(s.orientation || '').toLowerCase() === 'portrait' ? 'portrait' : 'landscape';
      groups.get(groupKey)[orient].push(s);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'ja'))
      .map(([groupName, { landscape, portrait }]) => ({
        groupName,
        subgroups: [
          ...(landscape.length ? [{ label: `${groupName}（横置き）`, schedules: pickLargestSchedule(landscape) }] : []),
          ...(portrait.length ? [{ label: `${groupName}（縦置き）`, schedules: pickLargestSchedule(portrait) }] : []),
        ]
      }));
  };

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/subscription/list`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch subscriptions');
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setError(t('dashboard.fetchError') || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load allowed schedules (already filtered by the server to this member's entitlements)
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setSchedulesLoading(true);
        const res = await fetch(`${API_BASE}/api/schedule/available`, { credentials: 'include' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          console.error('[Dashboard] /api/schedule/available error', res.status, body);
          throw new Error(`Failed to fetch schedules: ${res.status} ${body.error || ''}`);
        }
        const data = await res.json();
        console.log(`[Dashboard] schedules returned: ${(data.schedules||[]).length}`, data.schedules?.map(s=>s.id));
        const map = {};
        (data.schedules || []).forEach(s => { map[s.id] = s; });
        setScheduleMap(map);
      } catch (err) {
        console.error('[Dashboard] Failed to fetch schedules:', err);
      } finally {
        setSchedulesLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Default selection: first schedule for each subscription
  useEffect(() => {
    if (!subscriptions.length || schedulesLoading) return;
    setSelectedScheduleBySub(prev => {
      const next = { ...prev };
      subscriptions.forEach(sub => {
        const options = getSchedulesForSubscription(sub);
        if (!next[sub.subscriptionId] || !options.find(s => s.id === next[sub.subscriptionId])) {
          next[sub.subscriptionId] = options[0]?.id || '';
        }
      });
      return next;
    });
  }, [subscriptions, getSchedulesForSubscription, schedulesLoading]);

  const getSelectedSchedule = (subscription) => {
    const id = selectedScheduleBySub[subscription.subscriptionId];
    return scheduleMap[id] || null;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d)) return value;
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const handleViewBrowser = async (subscription, withDescription = false) => {
    try {
      const selectedSchedule = getSelectedSchedule(subscription);
      const res = await fetch(`${API_BASE}/api/auth/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Session-Id': `dashboard-${Date.now()}`
        },
        credentials: 'include',
        body: JSON.stringify({
          subscriptionId: subscription.subscriptionId,
          scheduleId: selectedSchedule?.id,
          slideshowDeviceMac: selectedSchedule?.slideshowDeviceMac,
          withDescription
        })
      });

      if (!res.ok) {
        const err = await res.json();
        if (err.error === 'CONCURRENT_SESSION') {
          alert(t('dashboard.concurrentSession'));
          return;
        }
        if (res.status === 401) {
          alert('セッションの有効期限が切れました。ページを更新してください。');
          window.location.reload();
          return;
        }
        throw new Error(err.message || err.error || 'Failed to start session');
      }

      const data = await res.json();
      const name = selectedSchedule?.name || selectedSchedule?.displayName || '';
      const url = name
        ? `${window.location.origin}${data.viewerUrl}&scheduleName=${encodeURIComponent(name)}`
        : `${window.location.origin}${data.viewerUrl}`;
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to start viewer session:', err);
      alert(t('dashboard.startSessionFailed'));
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>{t('dashboard.loading') || 'Loading subscriptions...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-branding">
          <img src="/yyslogo192.png" alt="YY Solutions" className="yys-logo" />
          <div>
            <h1>CALMU GALLERY</h1>
          </div>
        </div>
        <div className="header-actions">
          {onBackToModeSelection && (
            <button onClick={onBackToModeSelection} className="back-btn">
              ← {t('dashboard.back') || '戻る'}
            </button>
          )}
          <button
            onClick={() => { window.location.href = 'https://www.calmu.design/gallery'; }}
            className="logout-btn"
          >
            {t('dashboard.close') || '閉じる'}
          </button>
        </div>
      </div>

      {mode === 'browser' && (
        <div className="dashboard-content">
          {error && <div className="error-message">{error}</div>}

{subscriptions.length === 0 ? (
            <div className="no-subscriptions">
              <p>{t('dashboard.noSubscriptions') || 'サブスクリプションが見つかりません。管理者にお問い合わせください。'}</p>
            </div>
          ) : (
            <div className="subscriptions-list">
              <h3>{t('dashboard.yourSubscriptions') || 'Your Subscriptions'}</h3>
              {subscriptions.map(subscription => {
                const scheduleOptions = getSchedulesForSubscription(subscription);
                const selectedSchedule = getSelectedSchedule(subscription);
                return (
                  <div key={subscription.subscriptionId} className="subscription-card">
                    <div className="subscription-top">
                      <div className="subscription-info">
                        <h4 className="subscription-name">{subscription.planName}</h4>
                      </div>

                      <div className="subscription-details-inline">
                        <div className="schedule-selector-group">
                          <label className="schedule-label">{t('dashboard.selectedSchedule') || 'Schedule:'}</label>
                          <select
                            value={selectedScheduleBySub[subscription.subscriptionId] || ''}
                            onChange={e => {
                              setSelectedScheduleBySub(prev => ({
                                ...prev,
                                [subscription.subscriptionId]: e.target.value
                              }));
                            }}
                            className="schedule-select"
                            disabled={schedulesLoading || scheduleOptions.length === 0}
                          >
                            <option value="">{t('dashboard.selectSchedule') || 'スケジュールを選択...'}</option>
                            {groupSchedulesByGroup(scheduleOptions).flatMap(({ subgroups }) =>
                              subgroups.map(({ label, schedules: sg }) => (
                                <option key={sg[0].id} value={sg[0].id}>{label}</option>
                              ))
                            )}
                          </select>
                          {!schedulesLoading && scheduleOptions.length === 0 && (
                            <p className="subscription-id-subtext">このサブスクリプションに対応するスケジュールがありません。</p>
                          )}
                        </div>
                      </div>

                      <div className="subscription-actions">
                        <button
                          onClick={() => handleViewBrowser(subscription)}
                          className="action-btn browser-btn"
                          disabled={!selectedSchedule || (selectedSchedule.scheduleCategory !== 'browser' && !hasConfiguredMac(selectedSchedule))}
                        >
                          🖥️ {t('dashboard.startSlideshowButton') || 'スライドショー開始'}
                        </button>
                        <button
                          onClick={() => handleViewBrowser(subscription, true)}
                          className="action-btn browser-btn description-btn"
                          disabled={!selectedSchedule || (selectedSchedule.scheduleCategory !== 'browser' && !hasConfiguredMac(selectedSchedule))}
                        >
                          📝 {t('dashboard.startWithDescriptionButton') || '説明開始'}
                        </button>
                      </div>
                    </div>
                    <div className="subscription-footer">
                      <p className="subscription-footer-item">
                        <span className="subscription-id-subtext">開始日</span>
                        <span className="subscription-meta-value">{formatDate(subscription.startDate)}</span>
                      </p>
                      <p className="subscription-footer-item">
                        <span className="subscription-id-subtext">終了日</span>
                        <span className="subscription-meta-value">{formatDate(subscription.endDate)}</span>
                      </p>
                      <p className="subscription-footer-item">
                        <span className="subscription-id-subtext">契約番号</span>
                        <span className="subscription-meta-value">{subscription.subscriptionId}</span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {mode === 'device' && (
        <div className="dashboard-content" ref={devicesSectionRef}>
          <DeviceRegistration isTopLevel={true} />
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Dashboard;
